import axios from "axios";
import * as Sentry from "@sentry/react";
import keycloakInstance from "@/initKeycloak";
import { attachRetryOn401 } from "@/shared/api/interceptors/retryOn401";
import { contentHubUrl } from "./config";

/**
 * Axios client for the content-hub adapter's protected HTTP endpoints
 * (POST /v1/search, POST /v1/stream/configure).
 *
 * A request interceptor attaches the Keycloak access token — refreshing it first
 * if it's within 30s of expiry — to every request, mirroring the firehose and
 * headlines clients. `attachRetryOn401` refreshes the token and retries once on a
 * 401 (or an expired-token 403), so a token that lapses mid-session recovers
 * transparently.
 *
 * No `baseURL` is set: callers pass absolute URLs built with `contentHubUrl()`,
 * which preserves any sub-path in `VITE_CONTENT_HUB_ADAPTER_URL`.
 */
export const contentHubClient = axios.create({
  headers: { "Content-Type": "application/json" },
});

contentHubClient.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30); // refresh if expiring within 30s
    } catch {
      // Best-effort refresh — if it fails, the 401 retry interceptor handles it.
    }
    config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
  }
  return config;
});

contentHubClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      Sentry.captureException(new Error("Content-hub API returned 401"), {
        level: "warning",
        extra: {
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
        },
      });
    }
    return Promise.reject(error);
  }
);

attachRetryOn401(contentHubClient);

/**
 * Auth-less axios client for the content-hub adapter's PUBLIC endpoints
 * (POST /search/delayed — 24h-delayed stories for logged-out / expired-access
 * demo mode). No request interceptor, so no Keycloak token is ever attached:
 * the delayed feed is deliberately anonymous and must work with no session.
 */
export const contentHubPublicClient = axios.create({
  headers: { "Content-Type": "application/json" },
});

/** Adapter endpoint that mints a stream pass from the bearer token. */
const STREAM_PASS_ENDPOINT = "v1/stream/pass";
/** Query-param the /v1/stream endpoint expects the one-time pass under. */
const STREAM_PASS_PARAM = "pass";

/**
 * Exchange the user's Keycloak JWT for a short-lived (~30s), single-use stream
 * pass and return the SSE stream URL with that pass attached as a query param.
 *
 * The stream endpoint no longer takes the bearer token directly: instead
 * POST /v1/stream/pass — authenticated by the `contentHubClient` interceptor
 * (fresh token, 401-retry) — returns a url-safe pass that authorizes exactly
 * one stream connection. Native EventSource can't send an Authorization header,
 * so the (opaque, expiring) pass rides in the URL rather than the long-lived
 * token. Because the pass is single-use, callers must run this on *every*
 * (re)connect.
 *
 * @throws if the exchange fails (network/auth) or returns no usable pass — the
 *   caller should back off and retry the whole two-step flow.
 */
export async function fetchStreamPassUrl(streamUrl: string): Promise<string> {
  const { data } = await contentHubClient.post<{ pass?: string }>(
    contentHubUrl(STREAM_PASS_ENDPOINT)
  );

  const pass = data?.pass;
  if (!pass) {
    throw new Error("Content-hub /v1/stream/pass returned no pass");
  }

  const url = new URL(streamUrl);
  url.searchParams.set(STREAM_PASS_PARAM, pass);
  return url.toString();
}
